"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import type { MarketsCategory, MarketsPanelColumnItem } from "@/config/nav-links";
import { navIcons } from "./nav-icons";
import { cn } from "@/lib/utils";

type MarketsMegaMenuProps = {
    sidebarTitle?: string;
    categories: MarketsCategory[];
    onNavigate?: () => void;
    variant?: "desktop" | "mobile";
};

const flattenColumns = (columns: MarketsPanelColumnItem[][]) => columns.flat();

const panelItemClassName =
    "group flex w-full items-center gap-2 rounded-[var(--radius-m)] px-2 py-3 text-ink-header no-underline outline-none transition-colors hover:bg-neutral-50 hover:font-bold";

const PanelItemChevron = ({ alwaysVisible }: { alwaysVisible?: boolean }) => (
    <ChevronRight
        className={cn(
            "size-6 shrink-0 text-cta-primary transition-opacity",
            alwaysVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
        )}
        strokeWidth={1.5}
        aria-hidden
    />
);

const hasPanelItems = (category: MarketsCategory) =>
    flattenColumns(category.panel?.columns ?? []).length > 0;

type CategoryRowProps = {
    category: MarketsCategory;
    isActive: boolean;
    variant: "desktop" | "mobile";
    onHover: () => void;
    onSelect: () => void;
    onNavigate?: () => void;
};

const CategoryRow = ({
    category,
    isActive,
    variant,
    onHover,
    onSelect,
    onNavigate,
}: CategoryRowProps) => {
    const Icon = category.icon ? navIcons[category.icon] : null;
    const showPanel = hasPanelItems(category);
    const className =
        variant === "mobile"
            ? cn(
                  "flex w-full items-center gap-3 text-left text-xl leading-[26px] text-nav-text transition-colors",
                  isActive
                      ? "rounded-[var(--radius-m)] bg-nav-fg p-3 font-semibold"
                      : "rounded-[44px] px-4 py-3 font-regular hover:bg-nav-fg",
              )
            : cn(
                  "flex w-full items-center gap-3 rounded-[var(--radius-m)] px-4 py-3 text-left text-base leading-[22px] text-nav-text no-underline transition-colors hover:bg-nav-fg",
                  isActive ? "bg-nav-fg font-bold" : "font-regular",
              );

    const label = (
        <>
            {Icon && (
                <Icon className="size-6 shrink-0 text-nav-text" strokeWidth={1.5} aria-hidden />
            )}
            <span>{category.label}</span>
        </>
    );

    const newTabProps = category.openInNewTab
        ? { target: "_blank" as const, rel: "noopener noreferrer" }
        : {};

    const hoverProps = showPanel ? { onMouseEnter: onHover } : {};

    // Desktop: click follows href (Indices / Share Market Today). Hover still reveals panels.
    // Mobile: tap expands a panel when one exists; otherwise follow href.
    const followHrefOnClick = Boolean(category.href) && (variant === "desktop" || !showPanel);

    if (followHrefOnClick && category.href) {
        return (
            <Link
                href={category.href}
                className={className}
                onClick={onNavigate}
                {...hoverProps}
                {...newTabProps}
            >
                {label}
            </Link>
        );
    }

    return (
        <button
            type="button"
            className={className}
            onClick={onSelect}
            {...hoverProps}
        >
            {label}
        </button>
    );
};

const MarketsMegaMenu = ({
    sidebarTitle = "MARKETS",
    categories,
    onNavigate,
    variant = "desktop",
}: MarketsMegaMenuProps) => {
    const [activeLabel, setActiveLabel] = useState(categories[0]?.label ?? "");
    const activeCategory =
        categories.find((category) => category.label === activeLabel) ?? categories[0];

    if (!activeCategory) return null;

    if (variant === "mobile") {
        return (
            <div className="flex w-full flex-col gap-6 bg-nav-bg p-4">
                <p className="text-base font-regular uppercase leading-[22px] tracking-[var(--typography-font-letter-spacing-3xl)] text-nav-text">
                    {sidebarTitle}
                </p>

                <ul className="flex w-full flex-col gap-6">
                    {categories.map((category) => {
                        const isActive = category.label === activeCategory.label;
                        const panelItems = flattenColumns(category.panel?.columns ?? []);
                        const showPanel = isActive && panelItems.length > 0;

                        return (
                            <li key={category.label} className="flex w-full flex-col gap-4">
                                <CategoryRow
                                    category={category}
                                    isActive={isActive}
                                    variant="mobile"
                                    onHover={() => setActiveLabel(category.label)}
                                    onSelect={() => setActiveLabel(category.label)}
                                    onNavigate={onNavigate}
                                />

                                {showPanel && (
                                    <div className="flex w-full flex-col gap-5 overflow-hidden rounded-2xl bg-white p-5">
                                        <p className="text-base font-semibold uppercase leading-[22px] tracking-[2px] text-ink-header">
                                            {category.panel?.title}
                                        </p>
                                        <ul className="flex w-full flex-col gap-4">
                                            {panelItems.map((item) => (
                                                <li key={item.label}>
                                                    <Link
                                                        href={item.href}
                                                        onClick={onNavigate}
                                                        {...(item.openInNewTab
                                                            ? {
                                                                  target: "_blank" as const,
                                                                  rel: "noopener noreferrer",
                                                              }
                                                            : {})}
                                                        className="block w-full text-xl font-regular leading-[26px] text-ink-header no-underline"
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }

    const columns = activeCategory.panel?.columns ?? [];
    const activePanelItems = flattenColumns(columns);

    return (
        <div className="flex w-full items-start gap-4 bg-nav-bg p-4">
            <div className="flex w-full max-w-[290px] shrink-0 flex-col gap-6">
                <p className="text-base font-semibold uppercase leading-[22px] tracking-[var(--typography-font-letter-spacing-3xl)] text-nav-text">
                    {sidebarTitle}
                </p>
                <ul className="flex w-full flex-col gap-1">
                    {categories.map((category) => {
                        const isActive = category.label === activeCategory.label;

                        return (
                            <li key={category.label}>
                                <CategoryRow
                                    category={category}
                                    isActive={isActive}
                                    variant="desktop"
                                    onHover={() => setActiveLabel(category.label)}
                                    onSelect={() => setActiveLabel(category.label)}
                                    onNavigate={onNavigate}
                                />
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className="relative flex min-h-[290px] min-w-0 flex-1 flex-col gap-8 overflow-hidden rounded-2xl bg-white p-10">
                <div
                    aria-hidden
                    className="pointer-events-none absolute -right-6 -top-10 size-[140px] rounded-full bg-brand-primary-200/50 blur-2xl"
                />

                {activePanelItems.length > 0 ? (
                    <>
                        <p className="relative text-2xl font-semibold uppercase leading-8 tracking-[var(--typography-font-letter-spacing-3xl)] text-ink-header">
                            {activeCategory.panel?.title}
                        </p>

                        <div className="relative flex w-full items-start">
                            {columns.map((column, columnIndex) => (
                                <div key={columnIndex} className="flex min-w-0 flex-1 items-start">
                                    {columnIndex > 0 && (
                                        <div
                                            aria-hidden
                                            className="mx-[37.5px] hidden h-[290px] w-px shrink-0 bg-surface-stroke sm:block"
                                        />
                                    )}
                                    <ul className="flex w-full flex-col">
                                        {column.map((item) => (
                                            <li key={item.label}>
                                                <Link
                                                    href={item.href}
                                                    onClick={onNavigate}
                                                    {...(item.openInNewTab
                                                        ? {
                                                              target: "_blank" as const,
                                                              rel: "noopener noreferrer",
                                                          }
                                                        : {})}
                                                    className={cn(
                                                        panelItemClassName,
                                                        "text-base leading-[22px]",
                                                        item.featured && "font-bold",
                                                    )}
                                                >
                                                    {item.label}
                                                    <PanelItemChevron alwaysVisible={item.featured} />
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default MarketsMegaMenu;
